import { redactSensitiveData } from 'src/utils/security/redact-sensitive-data';

type BufferedBootError = {
  type: 'error' | 'unhandledrejection';
  message: string;
  source?: string;
  ts: number;
};

declare global {
  interface Window {
    __pcBoot?: { errors: BufferedBootError[] };
  }
}

let installed = false;

export type GlobalErrorEvent = {
  type: 'error' | 'unhandledrejection' | 'pre-mount';
  message: string;
  source?: string;
  error?: unknown;
};

type GlobalErrorListener = (event: GlobalErrorEvent) => void;

const errorListeners = new Set<GlobalErrorListener>();

/**
 * Watch every uncaught error and unhandled rejection the app sees, including
 * the pre-mount ones buffered by the boot safety net. Added so device
 * diagnostic logging can record failures without installing a second pair of
 * window handlers. A listener that throws is isolated.
 */
export function subscribeGlobalErrors(listener: GlobalErrorListener): () => void {
  errorListeners.add(listener);
  return () => {
    errorListeners.delete(listener);
  };
}

function notifyGlobalError(event: GlobalErrorEvent): void {
  const error =
    event.error instanceof Error
      ? { name: event.error.name, message: event.error.message, stack: event.error.stack }
      : event.error;
  errorListeners.forEach((listener) => {
    try {
      listener(redactSensitiveData({ ...event, error }));
    } catch {
      // Error reporting must never be broken by an error reporter.
    }
  });
}

/**
 * Surfaces boot-time failures that would otherwise be invisible on devices:
 * drains the pre-mount error buffer recorded by the boot-safety-net snippet in
 * the index*.html shells, then keeps logging uncaught errors and unhandled
 * rejections for the life of the page. Sentry is loaded lazily so apps that
 * never initialize it do not pay for it in their boot bundle; when it is not
 * initialized, captureMessage is a safe no-op.
 */
export function installGlobalErrorHandlers(): void {
  if (installed) return;
  installed = true;

  const buffered = (window.__pcBoot?.errors ?? []).map((entry) => redactSensitiveData(entry));
  buffered.forEach((entry) => {
    console.error('[boot] pre-mount error:', entry.type, entry.message, entry.source ?? '');
    notifyGlobalError({
      type: 'pre-mount',
      message: entry.message,
      source: entry.source,
    });
  });
  if (buffered.length > 0) {
    import('@sentry/react')
      .then((Sentry) => {
        buffered.forEach((entry) => {
          Sentry.captureMessage('boot: pre-mount error', {
            level: 'error',
            extra: { ...entry },
          });
        });
      })
      .catch(() => undefined);
  }

  // Sentry's own globalHandlers integration reports these when initialized;
  // the console.error is for builds where it is not (console.error survives
  // production builds - removeConsole only strips console.log).
  window.addEventListener('error', (event) => {
    console.error(
      '[global] uncaught error:',
      redactSensitiveData(event.message),
      redactSensitiveData(event.filename ?? '')
    );
    notifyGlobalError({
      type: 'error',
      message: event.message,
      source: event.filename ?? undefined,
      error: event.error,
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    console.error(
      '[global] unhandled rejection:',
      redactSensitiveData(event.reason instanceof Error ? event.reason.message : event.reason)
    );
    notifyGlobalError({
      type: 'unhandledrejection',
      message:
        event.reason instanceof Error ? event.reason.message : String(event.reason ?? 'unknown'),
      error: event.reason,
    });
  });
}
